import { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  styled,
  CSSObject,
  useTheme,
  useMediaQuery,
  useScrollTrigger,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import {
  brandColors,
  resolveColorToken,
} from "../../../../stories/helpers/ColorTokenHelper";
import { ColorTokensEnum } from "../../../../enums/tokens/colorTokensEnum";
import { TypographyTokensEnum } from "../../../../enums/tokens/typographyTokensEnum";
import typographyTokens from "../../../../stories/tokens/typography-tokens.json";
import FloatingActionButtonProps from "@/app/interfaces/props/FloatingActionButtonProps";
import { FabLinkTypeEnum } from "../../../../enums/fabLinkTypeEnum";
import { radii } from "../../../../stories/helpers/RadiusTokenHelper";
import { RadiusTokensEnum } from "../../../../enums/tokens/radiusTokensEnum";
import { elevations } from "../../../../stories/helpers/ElevationTokenHelper";
import { ElevationTokensEnum } from "../../../../enums/tokens/elevationTokensEnum";

const composeHref = (
  linkType: FabLinkTypeEnum | undefined,
  value: string | undefined
): string | undefined => {
  if (!linkType || linkType === FabLinkTypeEnum.NONE || !value?.trim()) {
    return undefined;
  }
  const trimmed = value.trim();
  const digits = trimmed.replace(/[^0-9+]/g, "");
  switch (linkType) {
    case FabLinkTypeEnum.PHONE:
      return `tel:${digits}`;
    case FabLinkTypeEnum.SMS:
      return `sms:${digits}`;
    case FabLinkTypeEnum.WHATSAPP:
      return `https://wa.me/${digits.replace(/^\+/, "")}`;
    case FabLinkTypeEnum.EMAIL:
      return `mailto:${trimmed}`;
    case FabLinkTypeEnum.URL:
      return /^[a-z]+:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    default:
      return undefined;
  }
};

const resolveTarget = (
  openInNewTab: boolean | undefined
): "_self" | "_blank" => (openInNewTab ? "_blank" : "_self");

const StyledFabContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isScrolled" && prop !== "isMobile" && prop !== "isTablet",
})<{ isScrolled: boolean; isMobile: boolean; isTablet: boolean }>(
  ({ isScrolled, isMobile, isTablet }) => ({
    position: "fixed",
    bottom: isScrolled ? "88px" : "24px",
    right: isMobile ? "28px" : isTablet ? "24px" : "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "8px",
    zIndex: 1000,
    pointerEvents: "none",
    transition: "bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "& > *": { pointerEvents: "auto" },
  })
);

const StyledFabButton = styled(IconButton, {
  shouldForwardProp: (prop) =>
    prop !== "bgColor" && prop !== "isMobile" && prop !== "isTablet",
})<{ bgColor?: string; isMobile: boolean; isTablet: boolean }>(
  ({ bgColor, isMobile, isTablet }) => {
    const size = isMobile ? "48px" : isTablet ? "56px" : "64px";
    const iconSize = isMobile ? "32px" : isTablet ? "36px" : "42px";
    return {
      width: size,
      height: size,
      backgroundColor: bgColor || brandColors[ColorTokensEnum.INTERACTIVE],
      color: brandColors[ColorTokensEnum.WHITE],
      borderRadius: radii[RadiusTokensEnum.PILL],
      padding: "0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: elevations[ElevationTokensEnum.ELEVATION_3],
      "&:hover": {
        backgroundColor: bgColor,
      },
      "& .MuiSvgIcon-root": {
        fontSize: iconSize,
      },
    };
  }
);

const StyledRow = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
});

const StyledRowTextContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "borderColor" && prop !== "isMobile" && prop !== "isTablet",
})<{ borderColor?: string; isMobile: boolean; isTablet: boolean }>(
  ({ borderColor, isMobile, isTablet }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "4px",
    paddingLeft: "24px",
    paddingRight: isMobile ? "32px" : "40px",
    backgroundColor: brandColors[ColorTokensEnum.WHITE],
    border: `2px solid ${
      borderColor || brandColors[ColorTokensEnum.INTERACTIVE]
    }`,
    borderRight: "none",
    borderTopLeftRadius: radii[RadiusTokensEnum.PILL],
    borderBottomLeftRadius: radii[RadiusTokensEnum.PILL],
    height: isMobile ? "48px" : isTablet ? "56px" : "64px",
    minWidth: isMobile ? "241px" : "285px",
    boxSizing: "border-box",
    boxShadow: elevations[ElevationTokensEnum.ELEVATION_3],
  })
);

const IconCircleContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "borderColor" && prop !== "isMobile" && prop !== "isTablet",
})<{ borderColor?: string; isMobile: boolean; isTablet: boolean }>(
  ({ borderColor, isMobile, isTablet }) => ({
    width: isMobile ? "48px" : isTablet ? "56px" : "64px",
    height: isMobile ? "48px" : isTablet ? "56px" : "64px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radii[RadiusTokensEnum.PILL],
    border: `2px solid ${
      borderColor || brandColors[ColorTokensEnum.INTERACTIVE]
    }`,
    backgroundColor: brandColors[ColorTokensEnum.WHITE],
    marginLeft: isMobile ? "-24px" : isTablet ? "-28px" : "-32px",
    zIndex: 2,
    boxSizing: "border-box",
    "& .MuiSvgIcon-root": {
      fontSize: isMobile ? "32px" : isTablet ? "36px" : "42px",
      color: borderColor || brandColors[ColorTokensEnum.INTERACTIVE],
    },
  })
);

export const FloatingActionButton = ({
  rows = [],
  initialOpen = false,
  mainIcon: MainIcon = SupportAgentIcon,
  buttonColor,
  line1Color,
  line2Color,
}: FloatingActionButtonProps) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const isScrolled = useScrollTrigger({
    threshold: 300,
    disableHysteresis: true,
  });

  const resolvedButtonColor = resolveColorToken(
    buttonColor,
    brandColors[ColorTokensEnum.INTERACTIVE]
  );
  const resolvedLine1Color = resolveColorToken(
    line1Color,
    brandColors[ColorTokensEnum.DARKEST_GREY]
  );
  const resolvedLine2Color = resolveColorToken(line2Color, resolvedButtonColor);

  const activeTypography = isMobile
    ? typographyTokens[TypographyTokensEnum.BODY_2_MEDIUM] || {}
    : typographyTokens[TypographyTokensEnum.BODY_1_MEDIUM] || {};

  return (
    <StyledFabContainer isScrolled={isScrolled} isMobile={isMobile} isTablet={isTablet}>
      {isOpen && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "8px",
          }}
        >
          {rows.map((row, index) => {
            const href = composeHref(row.line2LinkType, row.line2Value);
            const target = resolveTarget(row.line2OpenInNewTab);
            return (
            <StyledRow key={index}>
              <StyledRowTextContainer
                borderColor={resolvedButtonColor}
                isMobile={isMobile}
                isTablet={isTablet}
              >
                <Typography
                  sx={{
                    ...(activeTypography as CSSObject),
                    color:
                      resolvedLine1Color ||
                      brandColors[ColorTokensEnum.DARKEST_GREY],
                    margin: 0,
                  }}
                >
                  {row.line1}
                </Typography>
                <Typography
                  {...(href
                    ? {
                        component: "a",
                        href,
                        ...(target && { target }),
                        ...(target === "_blank" && {
                          rel: "noopener noreferrer",
                        }),
                      }
                    : {})}
                  sx={{
                    ...(activeTypography as CSSObject),
                    color:
                      resolvedLine2Color ||
                      resolvedButtonColor ||
                      brandColors[ColorTokensEnum.INTERACTIVE],
                    margin: 0,
                    ...(href && {
                      textDecoration: "none",
                      cursor: "pointer",
                    }),
                  }}
                >
                  {row.line2}
                </Typography>
              </StyledRowTextContainer>
              <IconCircleContainer
                borderColor={resolvedButtonColor}
                isMobile={isMobile}
                isTablet={isTablet}
              >
                <MainIcon />
              </IconCircleContainer>
            </StyledRow>
            );
          })}
        </Box>
      )}

      <StyledFabButton
        isMobile={isMobile}
        isTablet={isTablet}
        bgColor={resolvedButtonColor}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <CloseIcon /> : <MainIcon />}
      </StyledFabButton>
    </StyledFabContainer>
  );
};
